import axios, { AxiosResponse, InternalAxiosRequestConfig, AxiosResponseHeaders, AxiosError } from 'axios';
import {  prop, toLower, lensPath, set } from 'ramda';
import { debugLog } from '@/utils/customHooks/commonUtils';
import { BASE_URL } from '@/utils/constants';
import refreshTokenAPI from './user-service/refreshToken';

const WAF_SUPPORT_ID_HEADER = toLower('WAF-Support-ID'); // Axios converts all headers to lowercase

// Public endpoints that don't need authentication
const PUBLIC_ENDPOINTS = [
  '/api/customer/login',
  '/api/user/login',
  '/api/customer/sign-up',
  '/api/user/sign-up',
  '/api/auth/refresh',
  '/api/auth/password-reset/request',
  '/api/auth/password-reset/confirm',
];

const instance = axios.create({
  baseURL: BASE_URL,
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: Error | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  
  failedQueue = [];
};

const requestHandler = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    debugLog('API request', config);

    // Skip adding token for public endpoints
    const isPublicEndpoint = PUBLIC_ENDPOINTS.some(endpoint => 
      config.url?.includes(endpoint)
    );

    if (!isPublicEndpoint) {
      const token = localStorage.getItem('access_token');
      if (token) {
          config.headers.set('Authorization', `Bearer ${token}`);
      }
    }
    return config;
};

const responseHandler = (response: AxiosResponse): AxiosResponse => {
  debugLog('API response', response);
  const responseData = response.data || {}; // Ensure 'data' exists
  const { responseCode, message } = responseData;

  if (responseCode) {
    return set(lensPath(['data', 'message']), message || 'Success', response);
  }

  return response;
};

const errorHandler = async (error: unknown) => {
  const originalRequest = (error as AxiosError)?.config as InternalAxiosRequestConfig & { _retry?: boolean };
  
  if (axios.isAxiosError(error)) {
    debugLog('API error', {
      code: error.code,
      message: error.message,
      response: error.response,
      request: error.request,
      config: error.config,
    });

    // Handle 401 Unauthorized errors (token expired)
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      // Don't retry for public endpoints
      const isPublicEndpoint = PUBLIC_ENDPOINTS.some(endpoint => 
        originalRequest.url?.includes(endpoint)
      );

      if (isPublicEndpoint) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            // Update the token in the original request
            const newToken = localStorage.getItem('access_token');
            if (newToken && originalRequest.headers) {
              originalRequest.headers.set('Authorization', `Bearer ${newToken}`);
            }
            return instance(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refresh_token');

      if (!refreshToken) {
        // No refresh token available, logout user
        isRefreshing = false;
        processQueue(new Error('No refresh token available'));
        handleLogout();
        return Promise.reject(error);
      }

      try {
        // Attempt to refresh the token
        const response = await refreshTokenAPI(refreshToken);
        const { access, refresh } = response;

        // Update tokens in localStorage
        localStorage.setItem('access_token', access);
        if (refresh) {
          localStorage.setItem('refresh_token', refresh);
        }

        // Update the original request with new token
        if (originalRequest.headers) {
          originalRequest.headers.set('Authorization', `Bearer ${access}`);
        }

        isRefreshing = false;
        processQueue();

        // Retry the original request
        return instance(originalRequest);
      } catch (refreshError) {
        // Refresh token is also expired or invalid
        isRefreshing = false;
        processQueue(refreshError as Error);
        handleLogout();
        return Promise.reject(refreshError);
      }
    }
  } else {
    debugLog('Non-Axios error', error);
  }

  return Promise.reject(error);
};

const handleLogout = () => {
  // Clear all auth data
  localStorage.removeItem('user');
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  
  // Redirect to login page
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

const checkWafHeader = (headers: AxiosResponseHeaders | undefined) => {
  if (!headers) return;
  const wafSupportId = prop(WAF_SUPPORT_ID_HEADER, headers as AxiosResponseHeaders) as string | undefined;
  if (!wafSupportId) return;
  throw new Error(`requestBlockedByWaf: ${wafSupportId}`);
};

instance.interceptors.request.use(requestHandler, errorHandler);
instance.interceptors.response.use(
  (response: AxiosResponse) => {
    try {
      checkWafHeader(response.headers as AxiosResponseHeaders);
      return responseHandler(response);
    } catch (error) {
      return errorHandler(error);
    }
  },
  errorHandler
);

export default instance;